import React from "react";
import { Link, useLocation } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import useProfileStore from "../store/patient/profileStore";
import avatar from "../assets/img/user-avatar.png";
import { parseAvatar } from "../utils/chat/utils";

const pageMeta = [
  {
    matcher: (pathname) => pathname === "/" || pathname.includes("/dashboard/overview"),
    title: "Health Overview",
    description: "Track appointments, records, medications, and payments from one calm dashboard.",
  },
  {
    matcher: (pathname) => pathname.includes("appointment") || pathname.includes("myappointment"),
    title: "Appointments",
    description: "Schedule visits, review upcoming care, and stay on top of every booking.",
  },
  {
    matcher: (pathname) => pathname.includes("profile"),
    title: "My Profile",
    description: "Manage your personal details, account information, and care preferences.",
  },
  {
    matcher: (pathname) => pathname.includes("medications"),
    title: "Medications",
    description: "Review prescriptions, purchases, and medication history with clarity.",
  },
  {
    matcher: (pathname) => pathname.includes("examinations") || pathname.includes("lab") || pathname.includes("radiology"),
    title: "Results & Examinations",
    description: "Access lab requests, radiology, and medical reports in one elegant workspace.",
  },
  {
    matcher: (pathname) => pathname.includes("bills"),
    title: "Billing & Insurance",
    description: "Monitor invoices, claims, and wallet activity without the clutter.",
  },
  {
    matcher: (pathname) => pathname.includes("support"),
    title: "Support",
    description: "Reach care support quickly and get guided help whenever you need it.",
  },
];

function formatPathLabel(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const rawLabel = segments[segments.length - 1] || "dashboard";

  return rawLabel
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveHeaderMeta(pathname) {
  const matchedPage = pageMeta.find((item) => item.matcher(pathname));

  if (matchedPage) {
    return matchedPage;
  }

  const label = formatPathLabel(pathname);

  return {
    title: label,
    description: "Move through your health portal with a clearer, more modern experience.",
  };
}

export default function Header({ onSkin }) {
  const { pathname } = useLocation();
  const { profile, saveprofile } = useProfileStore();
  const { title, description } = resolveHeaderMeta(pathname);
  const todayLabel = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  }).format(new Date());

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <Link
      to=""
      ref={ref}
      onClick={(event) => {
        event.preventDefault();
        onClick(event);
      }}
      className="dropdown-link"
    >
      {children}
    </Link>
  ));

  const toggleSidebar = (event) => {
    event.preventDefault();
    const isOffset = document.body.classList.contains("sidebar-offset");

    if (isOffset) {
      document.body.classList.toggle("sidebar-show");
      return;
    }

    if (window.matchMedia("(max-width: 991px)").matches) {
      document.body.classList.toggle("sidebar-show");
      return;
    }

    document.body.classList.toggle("sidebar-hide");
  };

  const skinMode = (event) => {
    event.preventDefault();
    event.target.classList.add("active");

    let node = event.target.parentNode.firstChild;
    while (node) {
      if (node !== event.target && node.nodeType === Node.ELEMENT_NODE) {
        node.classList.remove("active");
      }
      node = node.nextElementSibling || node.nextSibling;
    }

    const skin = event.target.textContent.toLowerCase();
    const htmlTag = document.querySelector("html");

    if (skin === "dark") {
      htmlTag.setAttribute("data-skin", skin);
      localStorage.setItem("skin-mode", skin);

      if (typeof onSkin === "function") {
        onSkin(skin);
      }
      return;
    }

    htmlTag.removeAttribute("data-skin");
    localStorage.removeItem("skin-mode");

    if (typeof onSkin === "function") {
      onSkin("");
    }
  };

  const sidebarSkin = (event) => {
    event.preventDefault();
    event.target.classList.add("active");

    let node = event.target.parentNode.firstChild;
    while (node) {
      if (node !== event.target && node.nodeType === Node.ELEMENT_NODE) {
        node.classList.remove("active");
      }
      node = node.nextElementSibling || node.nextSibling;
    }

    const skin = event.target.textContent.toLowerCase();
    const htmlTag = document.querySelector("html");

    htmlTag.removeAttribute("data-sidebar");

    if (skin !== "default") {
      htmlTag.setAttribute("data-sidebar", skin);
      localStorage.setItem("sidebar-skin", skin);
      return;
    }

    localStorage.removeItem("sidebar-skin");
  };

  const handleLogout = () => {
    saveprofile(null);
    localStorage.setItem("user", null);
    localStorage.setItem("token", null);
    window.location.href = "/pages/signin2";
  };

  const fullName =
    `${profile?.other_names || ""} ${profile?.surname || ""}`.trim() || "Patient";

  return (
    <div className="header-main px-3 px-lg-4">
      <div className="header-shell">
        <div className="d-flex align-items-center gap-3 min-w-0">
          <Link to="" onClick={toggleSidebar} className="menu-link">
            <i className="ri-menu-2-fill"></i>
          </Link>

          <div className="header-intro min-w-0">
            <div className="header-chip">Patient Portal</div>
            <div className="header-copy-block min-w-0">
              <h1 className="header-title">{title}</h1>
              <p className="header-copy">{description}</p>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <div className="header-meta d-none d-xl-block">
            <small>Today</small>
            <strong>{todayLabel}</strong>
          </div>

          <Link to="/appointment/schedule" className="btn btn-primary quick-action-btn">
            <i className="ri-add-line"></i>
            <span>Book Visit</span>
          </Link>

          <Dropdown className="dropdown-skin" align="end">
            <Dropdown.Toggle as={CustomToggle}>
              <i className="ri-settings-3-line"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="mt-10-f">
              <label>Skin Mode</label>
              <nav className="nav nav-skin">
                <Link
                  to=""
                  onClick={skinMode}
                  className={localStorage.getItem("skin-mode") ? "nav-link" : "nav-link active"}
                >
                  Light
                </Link>
                <Link
                  to=""
                  onClick={skinMode}
                  className={localStorage.getItem("skin-mode") ? "nav-link active" : "nav-link"}
                >
                  Dark
                </Link>
              </nav>

              <hr />

              <label>Sidebar Skin</label>
              <nav id="sidebarSkin" className="nav nav-skin flex-wrap">
                <Link
                  to=""
                  onClick={sidebarSkin}
                  className={!localStorage.getItem("sidebar-skin") ? "nav-link active" : "nav-link"}
                >
                  Default
                </Link>
                <Link
                  to=""
                  onClick={sidebarSkin}
                  className={localStorage.getItem("sidebar-skin") === "prime" ? "nav-link active" : "nav-link"}
                >
                  Prime
                </Link>
                <Link
                  to=""
                  onClick={sidebarSkin}
                  className={localStorage.getItem("sidebar-skin") === "dark" ? "nav-link active" : "nav-link"}
                >
                  Dark
                </Link>
              </nav>
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown className="dropdown-profile" align="end">
            <Dropdown.Toggle as={CustomToggle}>
              <div className="avatar online">
                <img
                  src={profile?.profile_pic ? parseAvatar(profile.profile_pic) : avatar}
                  alt={fullName}
                />
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className="mt-10-f">
              <div className="dropdown-menu-body">
                <div className="avatar avatar-xl online mb-3">
                  <img
                    src={profile?.profile_pic ? parseAvatar(profile.profile_pic) : avatar}
                    alt={fullName}
                  />
                </div>
                <h5 className="mb-1 text-dark fw-semibold">{fullName}</h5>
                <p className="fs-sm text-secondary">Patient</p>

                <nav className="nav">
                  <Link to="/pages/profile">
                    <i className="ri-profile-line"></i>
                    View Profile
                  </Link>
                </nav>

                <hr />

                <nav className="nav">
                  <Link to="" onClick={handleLogout}>
                    <i className="ri-logout-box-r-line"></i>
                    Log Out
                  </Link>
                </nav>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}
